import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warning } from '../../../../../components/Alert/Alert';
import Input from '../../../../../components/Input/Input';
import { IOption, SelectCreate } from '../../../../../components/SelectCreate/SelectCreate';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { roundToTwo } from '../../../../MACRA_1/components/InputForm/Geometry/utils';
import { conversionLabel } from '../../../../conversion';
import { UnitOfMeasure } from '../../../../enum/project';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { selectProject } from '../../../selector/conversion';
import { setWeirGeometry } from '../../../store/project/projectSlice';
import { cleanResults } from '../../../store/project/resultsSlice';
import { EStepRatio } from '../../graphicalResults/crossSection/steppedWeir/ISteppedWeir';
import { IWeirGeometryProps, IWeirGeometrySteppedWeirForm } from './IWeirGeometry';
import './WeirGeometry.scss';

const ratioOptions: IOption[] = [
    { value: EStepRatio.oneToOne, label: '1 / 1' },
    { value: EStepRatio.oneToTwo, label: '1 / 2' },
    { value: EStepRatio.oneToThree, label: '1 / 3' },
    { value: EStepRatio.oneToFive, label: '1 / 5' },
];
const WeirGeometrySteppedWeir = (props: IWeirGeometryProps) => {
    let timer = useRef<NodeJS.Timeout | null>(null);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);

    const defaultValue: IWeirGeometrySteppedWeirForm = {
        foundationLayers: (project.data!.weirGeometry as IWeirGeometrySteppedWeirForm).foundationLayers,
        gabionWidth: (project.data!.weirGeometry as IWeirGeometrySteppedWeirForm).gabionWidth,
        stepHeight: (project.data!.weirGeometry as IWeirGeometrySteppedWeirForm).stepHeight,
        stepNumber: (project.data!.weirGeometry as IWeirGeometrySteppedWeirForm).stepNumber,
        stepRatio: (project.data!.weirGeometry as IWeirGeometrySteppedWeirForm).stepRatio,
    };

    const [form, setForm] = useState<IWeirGeometrySteppedWeirForm>(defaultValue);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        let name = e.target.name;
        let value = parseFloat(e.target.value);

        // input validation
        if (name == 'gabionWidth') {
            setForm({
                ...form,
                gabionWidth: handleStepOverLap(value, form.stepHeight, form.stepRatio!),
            });
        } else if (name == 'stepHeight') {
            setForm({
                ...form,
                gabionWidth: handleStepOverLap(form.gabionWidth!, value, form.stepRatio!),
                stepHeight: value,
            });
        } else if (name == 'foundationLayers') {
            let _foundationLayers = Math.trunc(value);
            if (_foundationLayers > form.stepNumber! - 1) {
                warning(`foundationLayers between 1 and ${form.stepNumber! - 1}`);
                _foundationLayers = form.stepNumber! - 1;
            } else if (_foundationLayers < 1) {
                warning(`foundationLayers between 1 and ${form.stepNumber! - 1}`);
                _foundationLayers = 1;
            }
            setForm({
                ...form,
                foundationLayers: _foundationLayers,
            });
        } else if (name == 'stepNumber') {
            if (value < 2) {
                warning(t('warning_not_be_lower', { value: 2 }));
                value = 2;
            }
            setForm({
                ...form,
                stepNumber: value,
            });
        }
    };

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            dispatch(cleanResults());
            dispatch(setWeirGeometry(form));
        }, 1000);
    }, [form]);

    const warningTimer = useRef<NodeJS.Timeout | null>(null);

    const handleStepOverLap = (gabionWidth: number, stepHeight: number, stepRatio: number): number => {
        let stepOverLap = gabionWidth - stepHeight / stepRatio;
        let oneToImp = isImperial ? 3 : 1;
        
        if (warningTimer.current) {
            clearTimeout(warningTimer.current);
            }
        if (stepOverLap < oneToImp) {
                warningTimer.current = setTimeout(() => {
                stepOverLap = oneToImp;
                gabionWidth = roundToTwo(stepHeight / stepRatio + stepOverLap);
                warning('set gabion width: ' + gabionWidth);
                setForm({
                    ...form,
                    gabionWidth: gabionWidth,
                });
            }, 3000);
        }

        return gabionWidth;
    };

    return (
        <div className='d-flex flex-column gap-2'>
            <Input type='number' title={t('input_stepNumber')} value={form.stepNumber} name='stepNumber' className='d-flex justify-content-between align-items-center' onChange={handleInputChange} />
            <div className='d-flex justify-content-between align-items-center'>
                <label htmlFor='ratio'>{t('input_ratio')}</label>
                <SelectCreate
                    value={form.stepRatio}
                    name='ratio'
                    options={ratioOptions}
                    parentOnChange={(newValue: any) =>
                        setForm({
                            ...form,
                            gabionWidth: handleStepOverLap(form.gabionWidth!, form.stepHeight, newValue),
                            stepRatio: newValue,
                        })
                    }
                />
            </div>
            <Input
                type='number'
                title={t('input_gabionWidth', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                value={form.gabionWidth || 0}
                name='gabionWidth'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_stepHeight', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                value={form.stepHeight}
                name='stepHeight'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_foundationLayers')}
                value={form.foundationLayers}
                name='foundationLayers'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
        </div>
    );
};

export default WeirGeometrySteppedWeir;
